/**
 * Utilities for test status handling
 */

export const OPENABLE_TEST_STATUSES = ['complete', 'failed', 'classification_failed'];

/**
 * Check if a test result can be opened/viewed from historical test lists
 * Tests that are in progress should only be accessed via the wizard
 */
export function canOpenTestResults(status: string): boolean {
  return OPENABLE_TEST_STATUSES.includes(status?.toLowerCase() ?? '');
}

/**
 * Get a user-friendly reason why a test cannot be opened
 */
export function getTestOpeningBlockReason(status: string): string | null {
  const lowerStatus = status?.toLowerCase() ?? '';
  
  if (OPENABLE_TEST_STATUSES.includes(lowerStatus)) {
    return null; // Can be opened
  }
  
  if (lowerStatus === 'cancelled') {
    return 'This test was cancelled';
  }
  
  if (lowerStatus.includes('baseline') || lowerStatus.includes('specimen') || lowerStatus === 'classifying') {
    return 'Test in progress';
  }
  
  return `Status: ${status}`;
}

/**
 * Check if classification can be re-triggered for a test.
 * Mirrors backend ForceRetriggerAsync eligibility:
 * - 'complete'              → re-classify to update/correct results
 * - 'classification_failed' → all retries exhausted, manual re-trigger required
 */
export function canRetriggerClassification(status: string): boolean {
  const s = status?.toLowerCase() ?? '';
  return s === 'complete' || s === 'classification_failed';
}

/**
 * Statuses a test can be calibrated from: its baseline and specimen measurements
 * have both finished, whatever happened in classification.
 * Mirrors backend ClassificationCalibrationService.EligibleStatuses.
 */
export const CALIBRATION_ELIGIBLE_STATUSES = ['specimen_completed', 'classifying', 'complete', 'classification_failed'];

export function isCalibrationEligible(status: string): boolean {
  return CALIBRATION_ELIGIBLE_STATUSES.includes(status?.toLowerCase() ?? '');
}

/** Turns a status code into words, e.g. "baseline_completed" → "baseline completed". */
export function formatTestStatus(status: string): string {
  return (status ?? '').replace(/_/g, ' ');
}

/**
 * Check if classification can be started for the first time.
 * Applies to specimen_completed tests (e.g. imported tests that have never been classified).
 */
export function canStartClassification(status: string): boolean {
  return (status?.toLowerCase() ?? '') === 'specimen_completed';
}
